import { c as _c } from "react/compiler-runtime";
import './AriaSelect.module.css';
import { NavArrowDown } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef } from 'react';
import { Button, FieldError, ListBox, ListBoxItem, type ListBoxItemProps, Popover, Select, SelectValue, Text } from 'react-aria-components';
import { Label } from '~/components/Label';
import { visuallyHidden } from '~/components/Utilities';
import styles from './AriaSelect.module.css';
import type { AriaSelectProps } from './AriaSelect.types';
function AriaSelect(t0) {
  const $ = _c(62);
  let ariaLabel;
  let children;
  let description;
  let errorMessage;
  let icon;
  let id;
  let isDisabled;
  let isRequired;
  let items;
  let label;
  let props;
  let selectedKey;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      label,
      description,
      errorMessage,
      children,
      items,
      displaySelectValue: t1,
      icon,
      selectionMode: t2,
      ariaLabel,
      selectedKey,
      variant: t3,
      isRequired,
      isDisabled,
      id,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = children;
    $[3] = description;
    $[4] = errorMessage;
    $[5] = icon;
    $[6] = id;
    $[7] = isDisabled;
    $[8] = isRequired;
    $[9] = items;
    $[10] = label;
    $[11] = props;
    $[12] = selectedKey;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    ariaLabel = $[1];
    children = $[2];
    description = $[3];
    errorMessage = $[4];
    icon = $[5];
    id = $[6];
    isDisabled = $[7];
    isRequired = $[8];
    items = $[9];
    label = $[10];
    props = $[11];
    selectedKey = $[12];
    t1 = $[13];
    t2 = $[14];
    t3 = $[15];
  }
  const displaySelectValue = t1 === undefined ? true : t1;
  const selectionMode = t2 === undefined ? "single" : t2;
  const variant = t3 === undefined ? "default" : t3;
  const t = useTranslations("aria");
  const t4 = variant === "pill";
  const t5 = variant === "legacy";
  let t6;
  if ($[16] !== t4 || $[17] !== t5) {
    t6 = clsx(styles.select, {
      [styles.pill]: t4,
      [styles.legacy]: t5
    });
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const className = t6;
  const listBoxRef = useRef(null);
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const listBox = listBoxRef.current;
      if (!listBox) {
        return;
      }
      const checkScrollable = () => {
        const isScrollable = listBox.scrollHeight > listBox.clientHeight;
        listBox.setAttribute("data-scrollable", isScrollable.toString());
      };
      checkScrollable();
      const observer = new ResizeObserver(checkScrollable);
      observer.observe(listBox);
      return () => observer.disconnect();
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== items) {
    t8 = [items];
    $[20] = items;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useEffect(t7, t8);
  const t9 = ariaLabel ?? (typeof label === "string" ? label : undefined);
  const t10 = id ?? `${label}-select-id`;
  const t11 = variant === "legacy" ? "small" : "large";
  let t12;
  if ($[22] !== isDisabled || $[23] !== isRequired || $[24] !== label || $[25] !== t10 || $[26] !== t11) {
    t12 = <Label id={t10} disabled={isDisabled} variant={t11} required={isRequired}>{label}</Label>;
    $[22] = isDisabled;
    $[23] = isRequired;
    $[24] = label;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = variant === "legacy";
  let t14;
  if ($[28] !== t13) {
    t14 = clsx(styles.button, {
      [styles.legacyButton]: t13
    });
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== ariaLabel || $[31] !== displaySelectValue) {
    t15 = <SelectValue className={styles.selectValue}>{props_0 => <>{props_0.isPlaceholder && ariaLabel || !displaySelectValue ? <span className={styles.title}>{ariaLabel}</span> : <span>{props_0.selectedText}</span>}</>}</SelectValue>;
    $[30] = ariaLabel;
    $[31] = displaySelectValue;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== icon) {
    t16 = icon ?? <NavArrowDown decorative={true} size="large" />;
    $[33] = icon;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = t16 as ReactNode;
  let t18;
  if ($[35] !== t17) {
    t18 = <span className={styles.selectIcon} aria-hidden="true">{t17}</span>;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t14 || $[38] !== t15 || $[39] !== t18) {
    t19 = <Button className={t14}>{t15}{t18}</Button>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== description || $[42] !== isRequired || $[43] !== t) {
    t20 = (description || isRequired) && <Text slot="description">{description}{isRequired && <span className={visuallyHidden}>{description ? `, ${t("required")}` : t("required")}</span>}</Text>;
    $[41] = description;
    $[42] = isRequired;
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== errorMessage) {
    t21 = <FieldError className={styles.fieldError}>{errorMessage}</FieldError>;
    $[45] = errorMessage;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== children || $[48] !== items || $[49] !== selectionMode) {
    t22 = <Popover className={styles.popover}><ListBox ref={listBoxRef} className={styles.listBox} selectionMode={selectionMode} items={items}>{children}</ListBox></Popover>;
    $[47] = children;
    $[48] = items;
    $[49] = selectionMode;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== className || $[52] !== isRequired || $[53] !== props || $[54] !== selectedKey || $[55] !== t12 || $[56] !== t19 || $[57] !== t20 || $[58] !== t21 || $[59] !== t22 || $[60] !== t9) {
    t23 = <Select selectedKey={selectedKey} aria-label={t9} className={className} isRequired={isRequired} {...props}>{t12}{t19}{t20}{t21}{t22}</Select>;
    $[51] = className;
    $[52] = isRequired;
    $[53] = props;
    $[54] = selectedKey;
    $[55] = t12;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
    $[59] = t22;
    $[60] = t9;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  return t23;
}
function SelectItem(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ListBoxItem className={styles.listBoxItem} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export { AriaSelect, SelectItem };